import { io } from 'socket.io-client';
import { SERVER_URL, TOKEN_KEY, storage } from '../api/client';
import api from '../api/endpoints';

/**
 * One socket connection per signed-in session, shared by every page.
 *
 * Used for dispatch (drivers get ride offers the instant they are made) and for
 * live tracking (the driver publishes its position, the customer's card moves).
 * Requires a long-lived server - EC2 behind Nginx, not serverless; the polling
 * fallback at the bottom of this file keeps tracking working when there is none.
 */
let socket = null;
let polledRide = null;      // trip a page is currently watching
const listeners = new Map(); // event -> Set(handler)

const emitLocal = (event, payload) => {
  const set = listeners.get(event);
  if (!set) return;
  set.forEach((fn) => {
    try { fn(payload); } catch (err) { /* a bad page handler must not kill the socket */ }
  });
};

export const connectRealtime = async () => {
  if (socket && socket.connected) return socket;

  const token = storage.get(TOKEN_KEY);
  if (!token) return null;

  if (socket) socket.close();
  socket = io(SERVER_URL, {
    auth: { token },
    // Long-polling first: a serverless host (Vercel) cannot hold a websocket
    // open, and leading with 'websocket' there means every attempt fails.
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 6000,
    /**
     * Give up after a handful of tries rather than retrying forever. Where
     * there is no long-lived server the socket can never connect, and endless
     * attempts just flood the console - the REST fallback below already keeps
     * tracking working, so failing quietly is the better behaviour.
     */
    reconnectionAttempts: 5,
    timeout: 12000,
  });

  socket.on('connect', () => {
    emitLocal('connection', { connected: true });
    // Room membership dies with the old connection - rejoin the watched trip.
    if (polledRide) socket.emit('ride:subscribe', { rideId: polledRide });
  });
  socket.on('disconnect', (reason) => emitLocal('connection', { connected: false, reason }));
  socket.on('connect_error', (err) => emitLocal('connection', { connected: false, error: err.message }));
  // Announced once, so a page can show "reconnecting" rather than hanging on a
  // live badge that will never light up.
  socket.io.on('reconnect_failed', () => emitLocal('connection', {
    connected: false,
    error: 'Realtime unavailable - falling back to polling',
    gaveUp: true,
  }));

  [
    'ride:offer', 'ride:accepted', 'ride:arrived', 'ride:started',
    'ride:completed', 'ride:cancelled', 'ride:no_drivers', 'ride:status', 'order:delivery', 'ride:taken',
    // Support chat. A new server event MUST be listed here or it silently
    // never reaches a page - this whitelist is the only path through.
    'ticket:message', 'ticket:status',
    // Rider is inside the arrival radius of the drop.
    'ride:near_drop',
    'driver:location', 'driver:status',
  ].forEach((event) => socket.on(event, (payload) => emitLocal(event, payload)));

  return socket;
};

export const disconnectRealtime = () => {
  if (socket) socket.close();
  socket = null;
  listeners.clear();
};

/** Subscribe to a server event. Returns an unsubscribe function for useEffect. */
export const onRealtime = (event, handler) => {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(handler);
  return () => {
    const set = listeners.get(event);
    if (set) set.delete(handler);
  };
};

export const emitRealtime = (event, payload) => { if (socket) socket.emit(event, payload); };

export const isRealtimeConnected = () => Boolean(socket && socket.connected);

/* ------------------------- convenience wrappers ------------------------- */

/** Join/leave one support conversation so its messages arrive live. */
export const joinTicket = (ticketId) => emitRealtime('ticket:subscribe', { ticketId });
export const leaveTicket = (ticketId) => emitRealtime('ticket:unsubscribe', { ticketId });

export const goOnline = (coordinates, vehicleType) =>
  emitRealtime('driver:online', { coordinates, vehicleType });

export const goOffline = () => emitRealtime('driver:offline');

export const publishLocation = (payload) => emitRealtime('driver:location', payload);

/* --------------------------- polling fallback --------------------------- */

/**
 * Serverless hosts cannot hold a socket open. While the socket is down we poll
 * the trip over REST and raise the same local events, so pages need no special
 * case - tracking just updates every few seconds instead of instantly.
 */
let pollTimer = null;
let lastStatus = null;
let lastDriverPing = null;

const STATUS_EVENT = {
  accepted: 'ride:accepted',
  arrived: 'ride:arrived',
  ongoing: 'ride:started',
  completed: 'ride:completed',
  cancelled: 'ride:cancelled',
  no_drivers: 'ride:no_drivers',
};

/*
 * Polling is ALWAYS on while a trip is watched - a connected socket that has
 * silently lost its room (every reconnect) delivers nothing. Duplicate pings
 * are dropped by timestamp.
 */
const pollTrip = async () => {
  if (!polledRide) return;
  try {
    const res = await api.rides.detail(polledRide);
    const ride = res.data;
    if (!ride) return;

    if (ride.status !== lastStatus) {
      lastStatus = ride.status;
      const payload = {
        rideId: polledRide,
        status: ride.status,
        ...(ride.driver ? {
          driver: {
            id: String(ride.driver._id),
            name: ride.driver.businessName,
            phone: ride.driver.phone,
            vehicle: ride.driver.vehicleDetails,
            rating: ride.driver.rating,
          },
        } : {}),
      };
      emitLocal('ride:status', payload);
      if (STATUS_EVENT[ride.status]) emitLocal(STATUS_EVENT[ride.status], payload);
    }

    const loc = ride.driverLocation;
    if (loc && loc.coordinates && loc.coordinates.length && loc.updatedAt !== lastDriverPing) {
      lastDriverPing = loc.updatedAt;
      emitLocal('driver:location', {
        rideId: polledRide,
        coordinates: loc.coordinates,
        heading: loc.heading || 0,
        at: new Date(loc.updatedAt).getTime(),
      });
    }
  } catch (err) {
    // Keep polling; a single failed request is not fatal.
  }
};

export const watchTrip = (rideId, { pollMs = 5000 } = {}) => {
  emitRealtime('ride:subscribe', { rideId });

  polledRide = rideId;
  lastStatus = null;
  lastDriverPing = null;
  clearInterval(pollTimer);
  pollTimer = setInterval(pollTrip, pollMs);
  pollTrip();
};

export const unwatchTrip = (rideId) => {
  emitRealtime('ride:unsubscribe', { rideId });
  clearInterval(pollTimer);
  pollTimer = null;
  polledRide = null;
};

/** True when updates are arriving over the socket rather than by polling. */
export const isLiveTracking = () => isRealtimeConnected();

const realtime = {
  connectRealtime, disconnectRealtime, onRealtime, emitRealtime,
  isRealtimeConnected, isLiveTracking, goOnline, goOffline,
  publishLocation, watchTrip, unwatchTrip,
};

export default realtime;
